import { useNavigate } from "react-router-dom";
import { ServiceRequestsIcon } from "../../../../../assets/icons";
import EmptyState from "../../../../../common/components/xepp/empty-state/EmptyState";
import UtilityPageLayout from "../../../component/layout/UtilityPageLayout";
import { ApplicationRoutes } from "../../../../../routes";
import styles from "./ServiceRequestTypesPage.module.scss";
import ServiceTypeCard from "../components/ServiceTypeCard/ServiceTypeCard";
import { useConfigLayout } from "../../../../../hooks/useConfigLayout";
import { useCallback, useEffect, useRef, useState } from "react";
import Button from "../../../../../common/components/xepp/button/Button";
import { useLazyGetServiceRequestsQuery } from "../../../../../service/customersupport-service";
import { useDispatch } from "react-redux";
import { resetState } from "../../../../../stores/slices/service-type-slice";
import { ServiceRequestType } from "../../../../../model/service-request/service-request";
import SearchInput from "../../../../../common/components/xepp/search-input/SearchInput";
import { useDebounce } from "../../../../../hooks/useDebounce";
import { hideLoader, showLoader } from "../../../../../stores/slices/loader-slice";

const PAGE_SIZE = 6;

const ServiceRequestTypesPage = () => {
	const navigator = useNavigate();
	const dispatch = useDispatch();

	const { updateLayout } = useConfigLayout();

	const [serviceTypes, setServiceTypes] = useState<ServiceRequestType[]>([]);
	const [currentPage, setCurrentPage] = useState(1);
	const [hasMore, setHasMore] = useState(true);
	const [searchTerm, setSearchTerm] = useState("");
	const observerTarget = useRef<HTMLDivElement>(null);

	const debouncedSearchTerm = useDebounce(searchTerm, 400);

	const [getServiceRequests, { isLoading, isFetching }] = useLazyGetServiceRequestsQuery();

	useEffect(() => {
		setCurrentPage(1);
		setHasMore(true);
		getServiceRequests({
			page: 1,
			pageSize: PAGE_SIZE,
			...(debouncedSearchTerm && { search: debouncedSearchTerm }),
		})
			.unwrap()
			.then(response => {
				setServiceTypes(response.items ?? []);
				setHasMore((response?.items?.length ?? 0) === PAGE_SIZE);
			})
			.catch(() => setHasMore(false));
	}, [getServiceRequests, debouncedSearchTerm]);

	const loadMore = useCallback(() => {
		if (isFetching || !hasMore) return;
		const nextPage = currentPage + 1;
		setCurrentPage(nextPage);

		getServiceRequests({
			page: nextPage,
			pageSize: PAGE_SIZE,
			...(debouncedSearchTerm && { search: debouncedSearchTerm }),
		})
			.unwrap()
			.then(response => {
				const newItems = response.items ?? [];
				setServiceTypes(prev => [...prev, ...newItems]);
				setHasMore(newItems.length === PAGE_SIZE);
			})
			.catch(() => setHasMore(false));
	}, [currentPage, isFetching, hasMore, getServiceRequests, debouncedSearchTerm]);

	// Infinite scroll observer
	useEffect(() => {
		const observer = new IntersectionObserver(
			entries => {
				if (entries[0].isIntersecting && hasMore && !isFetching) {
					loadMore();
				}
			},
			{ threshold: 0.1 }
		);

		const target = observerTarget.current;
		if (target) observer.observe(target);
		return () => {
			if (target) observer.unobserve(target);
		};
	}, [hasMore, isFetching, loadMore]);

	useEffect(() => {
		updateLayout({
			showCountrySelector: false,
			utility: null,
			showExportButton: false,
			showBreadCrumb: false,
		});
	}, [updateLayout]);

	// Show loader while initial loading
	if (isLoading && serviceTypes.length === 0) {
		return (
			<UtilityPageLayout title="Service Request Type">
				<div className={styles.loadMoreTrigger}>
					<div className={styles.loadingMore}>
						<div className={styles.spinner} />
						<span>Loading service request types...</span>
					</div>
				</div>
			</UtilityPageLayout>
		);
	}

	return (
		<>
			<UtilityPageLayout
				title="Service Request Type"
				middleComponent={<SearchInput placeholder="Search service request types" initialValue={searchTerm} onSearch={setSearchTerm} debounceTime={400} />}
				/* EKEDC: SR type creation disabled - statical SR definitions submitted through UI
			customButton={
					<Button
						text="Create New Service Request Type"
						variant="secondary"
						onClick={() => {
							dispatch(resetState());
							navigator(ApplicationRoutes.NEW);
						}}
					/>
				}
			*/
			>
				{serviceTypes.length === 0 && <EmptyState icon={<ServiceRequestsIcon height={50} width={50} />} description="No Service Requests Found" />}
				{serviceTypes.length > 0 && (
					<>
						<div className={styles.serviceTypesContainer}>
							{serviceTypes.map((serviceType, index) => (
								<ServiceTypeCard key={serviceType.id} serviceType={serviceType} index={index} />
							))}
						</div>
						{hasMore && (
							<div ref={observerTarget} className={styles.loadMoreTrigger}>
								{isFetching && (
									<div className={styles.loadingMore}>
										<div className={styles.spinner} />
										<span>Loading more...</span>
									</div>
								)}
							</div>
						)}
						{!hasMore && serviceTypes.length > 0 && <div className={styles.endOfList}>No more service request types to load</div>}
					</>
				)}
			</UtilityPageLayout>
		</>
	);
};

export default ServiceRequestTypesPage;
